import React, { useState } from "react";
import {
  ChevronDown, ChevronRight, Paperclip, Clock, Pencil, Trash2, AlertTriangle, ShieldAlert, RotateCcw,
  DollarSign, Workflow, Layers,
} from "lucide-react";
import { StatusBadge, PriorityBadge, DisciplineBadge, STATUS } from "../Badges";
import { InlineQuoteEdit } from "../InlineQuoteEdit";
import { fmtDateTime, fmtCalPeriod } from "../../lib/format";
import { toast } from "sonner";

// The customer/item-specific calibration cycle (e.g. Sandvik's 2-year
// override, Barloworld's standard 3-year cycle) — must be clearly visible
// to the technician working the line, not buried (SPT-PTE-2026-0056).
export function CalPeriodTag({ days, testId }) {
  const label = fmtCalPeriod(days);
  if (!label) return <span className="t-mut" data-testid={testId}>—</span>;
  return (
    <span className="badge b-gold" style={{ fontWeight: 700 }} data-testid={testId}>{label}</span>
  );
}

// Statuses a manager/admin can manually correct a line into. Excludes
// closed/paid, which must go through the proper settlement flow.
const OVERRIDE_STATUS_OPTIONS = Object.keys(STATUS).filter((s) => !["closed", "paid"].includes(s));

// Remarks/notes captured by Receiving at intake (condition + free-text
// handling notes) — previously invisible to technicians anywhere downstream
// (Worklist, Active Lab). Renders nothing for blank/placeholder values
// ("No Remarks" etc, common on legacy-imported records) so it only shows up
// when there's genuinely something to flag.
const _BLANK_NOTE = new Set(["", "no remarks", "none", "n/a", "na", "-", "- -"]);
function _hasNote(v) {
  return v && !_BLANK_NOTE.has(String(v).trim().toLowerCase());
}
export function LineNotes({ remarks, notes, testId }) {
  if (!_hasNote(remarks) && !_hasNote(notes)) return null;
  return (
    <div className="t-mut" style={{ fontSize: 11, fontStyle: "italic", marginTop: 2, maxWidth: 260 }}
      data-testid={testId}>
      {_hasNote(remarks) && <span>{remarks}</span>}
      {_hasNote(remarks) && _hasNote(notes) && <span> — </span>}
      {_hasNote(notes) && <span>{notes}</span>}
    </div>
  );
}

// Statuses where a technician can still record a bench outcome / request parts.
const WORK_OUTCOME_OK = new Set([
  "received", "assigned", "in_progress", "parts_required", "awaiting_spares",
  "parts_received", "awaiting_customer_approval", "awaiting_quote",
  "returned_from_subcontractor", "failed_calibration", "unable_to_calibrate",
]);
// Statuses where the item has effectively left the lab / is finalised.
const TERMINAL_STATUSES = new Set(["closed", "paid", "dispatched", "delivered", "collected"]);

// Build the per-line "Select action…" options, hiding anything not allowed for
// the current status.
//
// `simplifyAfterCalibration` (My Worklist only): ONE fixed-order menu that
// never varies by status, so a technician can chain Request Spares →
// Repaired → Adjusted → Calibrated → Send to Admin on the same line without
// any option disappearing partway through. "Start Work" is auto-triggered
// behind the scenes (see useLineActionRunner) instead of being a menu entry.
// Issuing the certificate / correcting the outcome is left to Active Lab /
// the Job Detail Modal (manager/admin), which always render the ORIGINAL
// per-status list below without the flag — that path is never actually
// lost, just moved off the technician's own queue.
export function lineActionOptions(item, { simplifyAfterCalibration = false } = {}) {
  const status = item.status;
  if (simplifyAfterCalibration) {
    if (TERMINAL_STATUSES.has(status) || status === "unserviceable_review") return [];
    return [
      ["parts_required", "Request Part / Spares"],
      ["repaired", "Mark Repaired"],
      ["adjusted", "Mark Adjusted"],
      ["calibrated", "Mark Calibrated"],
      ["unserviceable", "Mark Unserviceable"],
      ["return_to_customer", "Return to Customer (RtC)"],
      ["add_note", "Add Note"],
      ["send_to_admin", "Send to Admin"],
    ];
  }
  const opts = [];
  if (["received", "assigned"].includes(status)) opts.push(["start_work", "Start Work"]);
  if (WORK_OUTCOME_OK.has(status)) {
    opts.push(["calibrated", "Mark Calibrated"]);
    opts.push(["adjusted", "Mark Adjusted"]);
    opts.push(["repaired", "Mark Repaired"]);
    opts.push(["parts_required", "Request Part / Spares"]);
  } else if (["calibrated", "adjusted", "repaired"].includes(status)) {
    // Bench work is done — issue the certificate, re-verify, rework, or hand off.
    // A single item can legitimately need MORE than one outcome recorded in
    // sequence (e.g. repaired AND adjusted AND calibrated) — so keep offering
    // every outcome other than the one it's currently sitting on, not just
    // "Mark Calibrated".
    if (!item.certificate_number) opts.push(["issue_certificate", "Issue Certificate"]);
    if (status !== "calibrated") opts.push(["calibrated", "Mark Calibrated"]);
    if (status !== "adjusted") opts.push(["adjusted", "Mark Adjusted"]);
    if (status !== "repaired") opts.push(["repaired", "Mark Repaired"]);
    opts.push(["back_to_progress", "Send back to In Progress (rework)"]);
    opts.push(["send_to_admin", "Send to Admin"]);
  }
  if (!TERMINAL_STATUSES.has(status) && status !== "unserviceable_review") {
    opts.push(["unserviceable", "Mark Unserviceable"]);
    opts.push(["return_to_customer", "Return to Customer (RtC)"]);
  }
  if (!["closed", "paid"].includes(status)) opts.push(["add_note", "Add Note"]);
  return opts;
}

// Marks a technician-menu option with a completion/current-stage cue so a
// tech can tell where a line is in the pipeline WITHOUT the option list
// itself changing shape (SPT — added after the fixed 8-option menu made the
// dropdown no longer imply status by what it hides/shows).
// ✓ = this outcome has already been recorded at some point in the line's
//     history (a line can legitimately pass through more than one outcome).
// ● = this IS the line's current status right now.
const _STAGE_STATUSES = {
  parts_required: ["parts_required", "awaiting_spares", "parts_received"],
  repaired: ["repaired"],
  adjusted: ["adjusted"],
  calibrated: ["calibrated"],
};
function _stageMarker(item, actionKey) {
  const mapped = _STAGE_STATUSES[actionKey];
  if (!mapped) return "";
  if (mapped.includes(item.status)) return "● ";
  const done = (item.status_history || []).some((h) => mapped.includes(h.status));
  return done ? "✓ " : "";
}

// One dropdown + Go per equipment line — the simplified technician flow.
export function LineActionPicker({ item, onRun, busy, simplifyAfterCalibration }) {
  const [sel, setSel] = useState("");
  const opts = lineActionOptions(item, { simplifyAfterCalibration });
  if (!opts.length) return <span className="t-mut" style={{ fontSize: 12 }}>—</span>;
  const go = () => {
    if (!sel) { toast.error("Please select an action first."); return; }
    onRun(sel, item);
    setSel("");
  };
  return (
    <div className="line-action-control">
      {simplifyAfterCalibration && (
        <span style={{ marginRight: 6, display: "inline-flex" }} data-testid={`job-line-current-status-${item.id}`}>
          <StatusBadge status={item.status} />
        </span>
      )}
      <select className="line-action-select" value={sel}
        onChange={(e) => setSel(e.target.value)}
        data-testid={`job-line-action-select-${item.id}`}>
        <option value="">Select action…</option>
        {opts.map(([v, l]) => (
          <option key={v} value={v}>{simplifyAfterCalibration ? `${_stageMarker(item, v)}${l}` : l}</option>
        ))}
      </select>
      <button className="btn sm gold" onClick={go} disabled={busy}
        data-testid={`job-line-action-go-${item.id}`}>Go</button>
    </div>
  );
}

export function StatusTimeline({ history = [] }) {
  if (!history.length) return <div className="t-mut" style={{ fontSize: 12 }}>No history yet.</div>;
  return (
    <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
      {history.map((h, i) => (
        <li key={`${h.at || ""}-${i}`} style={{ display: "flex", gap: 8, fontSize: 12 }}>
          <Clock size={11} style={{ marginTop: 4, color: "var(--text-3)", flex: "none" }} />
          <div>
            <span className="t-strong">{h.status}</span>
            {h.from_status && h.from_status !== "auto" && <span className="t-mut"> from {h.from_status}</span>}
            <span className="t-mut"> · {fmtDateTime(h.at)} · {h.by_name || h.by || "—"}</span>
            {h.note && <div className="t-mut" style={{ marginTop: 2, fontStyle: "italic" }}>“{h.note}”</div>}
            {h.manual_override && <span className="badge b-amber" style={{ marginTop: 2, fontSize: 9.5 }}>Manual correction</span>}
          </div>
        </li>
      ))}
    </ol>
  );
}

// Manager/admin-only tool to manually correct a line's status when it drifted
// out of sync with reality (e.g. left "Received" though a tech already has
// it, or vice versa) — bypasses the normal workflow triggers but requires a
// note and is clearly flagged in the status history for audit purposes.
function StatusOverride({ item, onApply, busy }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(item.status);
  const [note, setNote] = useState("");
  if (!open) {
    return (
      <button className="btn ghost sm" onClick={() => { setStatus(item.status); setOpen(true); }}
        data-testid={`job-line-override-open-${item.id}`}
        title="Manually correct this line's status">
        <ShieldAlert size={11} /> Correct status
      </button>
    );
  }
  const apply = () => {
    if (!note.trim()) { toast.error("Please add a note explaining the correction."); return; }
    if (status === item.status) { toast.message("Pick a different status to correct to."); return; }
    onApply(item, status, note.trim());
    setOpen(false); setNote("");
  };
  return (
    <div className="card pad" style={{ marginTop: 10, background: "var(--card-2, #fff)" }} data-testid={`job-line-override-panel-${item.id}`}>
      <div className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 6 }}>MANUAL STATUS CORRECTION</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select className="line-action-select" value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid={`job-line-override-select-${item.id}`}>
          {OVERRIDE_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS[s][0]}</option>)}
        </select>
        <input type="text" placeholder="Why is this being corrected? (required)"
          value={note} onChange={(e) => setNote(e.target.value)}
          data-testid={`job-line-override-note-${item.id}`}
          style={{ flex: "1 1 220px", minWidth: 180 }} />
        <button className="btn sm gold" onClick={apply} disabled={busy}
          data-testid={`job-line-override-apply-${item.id}`}>Apply</button>
        <button className="btn sm ghost" onClick={() => { setOpen(false); setNote(""); }}
          data-testid={`job-line-override-cancel-${item.id}`}>Cancel</button>
      </div>
    </div>
  );
}

// SPT — items sharing the same group_number (e.g. equipment tracked/shipped
// as one physical set, like a torque-wrench kit) render clustered together
// under a small group header, instead of being scattered in received order.
// A group_number shared by only 1 surviving item (e.g. after another line in
// the group was removed) just renders as a normal standalone row.
function clusterByGroup(items) {
  const clusters = [];
  const indexOf = new Map(); // group_number -> cluster index
  for (const it of items) {
    const gn = (it.group_number || "").trim();
    if (gn && indexOf.has(gn)) {
      clusters[indexOf.get(gn)].items.push(it);
    } else {
      if (gn) indexOf.set(gn, clusters.length);
      clusters.push({ group_number: gn || null, items: [it] });
    }
  }
  return clusters;
}

/**
 * Job equipment-line items table — selection checkboxes (non-technicians get
 * the full dispatch/payment bulk bar; technicians get row checkboxes too so
 * they can bulk "Send to Admin" a mixed batch of calibrated/adjusted/repaired
 * lines after capturing each line's own notes individually — SPT-PTE-2026-0028),
 * action picker per line, expand to show status timeline + work details.
 */
export function JobLinesTable({
  items, expanded, setExpanded,
  selected, toggle, toggleAll,
  showSelect, isTechnician, isManager, canAct, canEdit, canDispatch,
  busy,
  onOpenEquip, onEditLine, onRunAction, onReview, onOverrideStatus, onRemoveLine, onReopenLine,
  onReturnToSubcon, onMarkPaid, onLineChanged,
}) {
  // Technicians can select rows too (for the bulk "Send to Admin" toolbar
  // rendered by the parent), even though they never see the admin/dispatch
  // bulk bar itself.
  const canSelectRows = showSelect || isTechnician;
  // Column count varies by role (technician sees a slimmer table).
  const colCount =
    (canSelectRows ? 1 : 0) +
    (!isTechnician ? 1 : 0) +   // priority dot
    1 +                          // equipment
    (isTechnician ? 2 : 2) +     // tech: Serial/Issue + Discipline · manager: Serial # + Issue #
    (!isTechnician ? 1 : 0) +    // technician
    1 +                          // status
    1 +                          // cal period
    1 +                          // cert (reserved or issued)
    1 +                          // action
    1;                           // expand

  return (
    <div className="tbl-wrap">
      <table>
        <thead>
          <tr>
            {canSelectRows && (
              <th style={{ width: 28 }}>
                <input type="checkbox" data-testid="job-select-all"
                  checked={selected.size === items.length && items.length > 0}
                  onChange={() => toggleAll(items)} />
              </th>
            )}
            {!isTechnician && <th style={{ width: 22 }}></th>}
            <th>Equipment</th>
            {isTechnician ? (
              <>
                <th>Serial / Issue</th>
                <th>Discipline</th>
              </>
            ) : (
              <>
                <th>Serial #</th>
                <th>Issue #</th>
              </>
            )}
            {!isTechnician && <th>Technician</th>}
            <th>Current Status</th>
            <th>Cal Period</th>
            <th>Cert / CR #</th>
            <th>Action</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {clusterByGroup(items).map((cluster) => {
            const clustered = cluster.items.length > 1;
            return (
              <React.Fragment key={cluster.group_number || cluster.items[0].id}>
                {clustered && (
                  <tr className="job-line-group-header" data-testid={`job-line-group-header-${cluster.group_number}`}>
                    <td colSpan={colCount} style={{ background: "var(--gold-bg, #fdf6e3)", padding: "5px 10px", borderTop: "1px solid var(--line)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: "var(--gold-deep, #8a6d1e)" }}>
                        <Layers size={11} /> GROUP {cluster.group_number} · {cluster.items.length} lines
                      </span>
                    </td>
                  </tr>
                )}
                {cluster.items.map((it) => {
            const isOpen = !!expanded[it.id];
            const isUrgent = it.priority === "urgent" || it.priority === "high";
            return (
              <React.Fragment key={it.id}>
                <tr data-testid={`job-line-${it.id}`}
                  style={clustered ? { background: "var(--gold-bg-soft, #fffaf0)" } : undefined}>
                  {canSelectRows && (
                    <td><input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)} data-testid={`job-select-${it.id}`} /></td>
                  )}
                  {!isTechnician && (
                    <td>
                      {it.priority === "urgent" && <span className="prio-dot prio-urgent" title="Urgent" />}
                      {it.priority === "high" && <span className="prio-dot prio-high" title="High" />}
                    </td>
                  )}
                  <td>
                    <button className="link-row" onClick={() => it.equipment_id && onOpenEquip(it.equipment_id)}
                      style={{ background: "none", border: 0, padding: 0, textAlign: "left", cursor: it.equipment_id ? "pointer" : "default" }}
                      data-testid={`job-line-open-${it.id}`}>
                      <div className="t-strong">{it.description || "—"}</div>
                      {(it.attachments?.length > 0) && (
                        <div className="mono t-mut" style={{ fontSize: 11.5 }}>
                          <Paperclip size={10} style={{ verticalAlign: "middle" }} /> {it.attachments.length}
                        </div>
                      )}
                    </button>
                  </td>
                  {isTechnician ? (
                    <>
                      <td className="mono t-mut" style={{ fontSize: 12 }}>
                        {it.serial_number ? "SN " + it.serial_number : it.issue_number ? "IN " + it.issue_number : "—"}
                      </td>
                      <td><DisciplineBadge name={it.discipline} /></td>
                    </>
                  ) : (
                    <>
                      <td className="mono" style={{ fontSize: 12 }}>{it.serial_number || "—"}</td>
                      <td className="mono" style={{ fontSize: 12 }}>{it.issue_number || "—"}</td>
                    </>
                  )}
                  {!isTechnician && <td><span className="t-mut">{it.technician_name || "—"}</span></td>}
                  <td>
                    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                      <StatusBadge status={it.status} />
                      {isUrgent && <PriorityBadge priority={it.priority} />}
                    </span>
                  </td>
                  <td>
                    <CalPeriodTag days={it.calibration_period_days} testId={`job-line-calperiod-${it.id}`} />
                  </td>
                  <td className="mono" data-testid={`job-line-cert-${it.id}`}>
                    {it.certificate_number
                      ? it.certificate_number
                      : it.reserved_certificate_number
                        ? <span className="t-mut" title="Reserved at Receiving — will be stamped when the certificate is issued">{it.reserved_certificate_number} <span style={{ fontSize: 10.5 }}>(reserved)</span></span>
                        : "—"}
                    {!isTechnician && (it.quote_number || it.invoice_number || it.purchase_order || canEdit) && (
                      <div className="t-mut" style={{ fontSize: 10.5, fontStyle: "italic", marginTop: 3, display: "flex", alignItems: "center", gap: 3 }}
                        data-testid={`job-line-billing-${it.id}`}>
                        {canEdit ? (
                          <span style={{ display: "inline-flex", alignItems: "center" }}>
                            Q <InlineQuoteEdit item={it} onSaved={onLineChanged} testidPrefix="job-line-quote" />
                          </span>
                        ) : (it.quote_number && <span>Q {it.quote_number}</span>)}
                        {it.purchase_order && <span>{" · "}PO {it.purchase_order}</span>}
                        {it.invoice_number && <span>{" · "}Inv {it.invoice_number}</span>}
                      </div>
                    )}
                  </td>
                  <td>
                    {canAct && (
                      isManager && it.status === "unserviceable_review" ? (
                        <button className="btn sm gold"
                          onClick={() => onReview(it.id)}
                          title="Open unserviceable review"
                          data-testid={`job-line-review-usr-${it.id}`}>
                          <AlertTriangle size={11} /> Review
                        </button>
                      ) : (
                        <div className="line-action-cell">
                          {!isTechnician && canEdit && (
                            <button className="icon-btn" title="Edit line"
                              onClick={() => onEditLine(it)}
                              data-testid={`job-line-edit-${it.id}`}>
                              <Pencil size={12} />
                            </button>
                          )}
                          {isManager && onRemoveLine && (
                            <button className="icon-btn" title="Remove line"
                              onClick={() => onRemoveLine(it)}
                              data-testid={`job-line-remove-${it.id}`}>
                              <Trash2 size={12} />
                            </button>
                          )}
                          {isManager && onReopenLine && ["closed", "paid"].includes(it.status) ? (
                            <button className="btn sm ghost" title="Re-open this line only"
                              onClick={() => onReopenLine(it)}
                              data-testid={`job-line-reopen-${it.id}`}>
                              <RotateCcw size={11} /> Re-open
                            </button>
                          ) : (
                            <LineActionPicker item={it} onRun={onRunAction} busy={busy} />
                          )}
                          {canDispatch && onMarkPaid
                            && ["dispatched", "delivered", "collected"].includes(it.status)
                            && !["paid", "not_required"].includes(it.payment_status) && (
                            <button className="btn sm gold" title="Record this line's billing info and mark it paid"
                              onClick={() => onMarkPaid(it)}
                              data-testid={`job-line-mark-paid-${it.id}`}>
                              <DollarSign size={11} /> Mark Paid
                            </button>
                          )}
                        </div>
                      )
                    )}
                    {(isManager || canDispatch) && onReturnToSubcon && ["closed", "paid"].includes(it.status) && (
                      <button className="btn sm ghost" title="Send this line back to a subcontractor (e.g. replacement sample)"
                        onClick={() => onReturnToSubcon(it)}
                        data-testid={`job-line-return-subcon-${it.id}`}
                        style={{ marginTop: canAct ? 4 : 0 }}>
                        <Workflow size={11} /> Send to Subcon
                      </button>
                    )}
                  </td>
                  <td>
                    <button className="icon-btn" onClick={() => setExpanded((e) => ({ ...e, [it.id]: !e[it.id] }))}
                      data-testid={`job-line-expand-${it.id}`} title="Show status history">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={colCount} style={{ background: "var(--bg-soft, #fafafa)" }}>
                      <div style={{ padding: "8px 12px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
                          <div className="t-mut" style={{ fontSize: 11.5, fontWeight: 600 }}>STATUS TIMELINE</div>
                          {isManager && <StatusOverride item={it} onApply={onOverrideStatus} busy={busy} />}
                        </div>
                        <StatusTimeline history={it.status_history || []} />
                        {(it.calibration_result || it.repair_time_minutes != null || it.fuses_used || it.batteries_used || it.replaced_with || (it.workbench_outcomes || []).length > 0) && (
                          <div style={{ marginTop: 10, fontSize: 12 }} data-testid={`job-work-details-${it.id}`}>
                            <div className="t-mut" style={{ fontWeight: 600, marginBottom: 4 }}>WORK DETAILS <span style={{ fontWeight: 400, fontSize: 10.5 }}>(staff / quote — not shown to customer)</span></div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, rowGap: 4 }}>
                              {(it.workbench_outcomes || []).length > 0 && <span><b>Outcome:</b> {(it.workbench_outcomes || []).join(", ")}</span>}
                              {!it.workbench_outcomes?.length && it.calibration_result && <span><b>Result:</b> {it.calibration_result}</span>}
                              {it.repair_time_minutes != null && <span><b>Repair time:</b> {it.repair_time_minutes} min</span>}
                              {it.fuses_used && <span><b>Fuses:</b> {it.fuses_used}</span>}
                              {it.batteries_used && <span><b>Batteries:</b> {it.batteries_used}</span>}
                              {it.replaced_with && <span><b>Replaced/fitted:</b> {it.replaced_with}</span>}
                            </div>
                          </div>
                        )}
                        {it.technician_notes && (
                          <div style={{ marginTop: 10, fontSize: 12 }}>
                            <div className="t-mut" style={{ fontWeight: 600, marginBottom: 4 }}>TECH NOTES</div>
                            <div className="t-mut" style={{ whiteSpace: "pre-wrap" }}>{it.technician_notes}</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
                })}
              </React.Fragment>
            );
          })}
          {items.length === 0 && <tr><td colSpan={colCount} className="t-mut">No lines on this job.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
